import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../i18n';
import { PageHeader } from '../components/layout/AppShell';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { Skeleton } from '../components/ui/Skeleton';
import { IconDownload, IconStore } from '../components/ui/icons';
import { licenses as licensesApi } from '../lib/api';
import { track } from '../lib/analytics';
import { apiErrorMessage } from '../lib/errors';
import type { License } from '../lib/types';

type LicenseState = 'ok' | 'revoked' | 'expired' | 'exhausted';

/** A license is downloadable only while not revoked, not expired, and not out of downloads. */
export function licenseState(lic: License, now = Date.now()): LicenseState {
  if (lic.revoked_at) return 'revoked';
  if (lic.expires_at && new Date(lic.expires_at).getTime() <= now) return 'expired';
  if (lic.max_downloads !== null && lic.downloads_used >= lic.max_downloads) return 'exhausted';
  return 'ok';
}

export function Purchases() {
  const { t, fmt } = useI18n();
  const [items, setItems] = useState<License[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    track('purchase_page_viewed', {});
    const controller = new AbortController();
    licensesApi
      .mine(controller.signal)
      .then((res) => setItems(res.licenses))
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(apiErrorMessage(err, t));
      });
    return () => controller.abort();
  }, [t]);

  return (
    <div>
      <PageHeader title={t.purchases.title} subtitle={t.purchases.subtitle} />

      {error ? (
        <p className="type-body-sm text-danger" role="alert">
          {error}
        </p>
      ) : items === null ? (
        <div className="flex max-w-2xl flex-col gap-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<IconStore size={48} />}
          title={t.purchases.emptyTitle}
          body={t.purchases.emptyBody}
          action={
            <Link to="/market">
              <Button size="lg">{t.purchases.emptyCta}</Button>
            </Link>
          }
        />
      ) : (
        <ul className="flex max-w-2xl flex-col gap-3">
          {items.map((lic) => {
            const state = licenseState(lic);
            const disabled = state !== 'ok' || !lic.download_url;
            const stateLabel =
              state === 'revoked'
                ? t.purchases.stateRevoked
                : state === 'expired'
                  ? t.purchases.stateExpired
                  : state === 'exhausted'
                    ? t.purchases.stateExhausted
                    : null;
            const remaining =
              lic.max_downloads === null
                ? t.purchases.unlimited
                : t.purchases.downloadsLeft(Math.max(0, lic.max_downloads - lic.downloads_used));
            return (
              <li key={lic.id}>
                <Card className={`p-4 ${disabled ? 'opacity-60' : ''}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="type-heading-2 text-primary truncate">{lic.listing_title ?? lic.asset_title ?? '—'}</p>
                      {lic.asset_title && lic.listing_title !== lic.asset_title ? (
                        <p className="type-body-sm text-secondary truncate">{lic.asset_title}</p>
                      ) : null}
                      <p className="type-caption text-muted mt-1">
                        {t.purchases.licenseLine(t.listing.license[lic.license_type])} · {remaining}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-2">
                      <span className="type-price text-accent tnum">{fmt.money(lic.total_cents, lic.currency)}</span>
                      {stateLabel ? (
                        <span className="type-label text-danger">{stateLabel}</span>
                      ) : lic.download_url ? (
                        <a
                          href={licensesApi.downloadHref(lic.download_url)}
                          className="focus-ring inline-flex h-10 items-center justify-center gap-2 rounded border border-accent px-4 font-mono text-[13px] font-bold uppercase tracking-[0.04em] text-accent transition-colors duration-fast ease-machine hover:bg-accent-tint"
                        >
                          <IconDownload size={16} />
                          {t.purchases.download}
                        </a>
                      ) : null}
                      <a
                        href={licensesApi.certificateHref(lic.id)}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => track('certificate_viewed', { license_id: lic.id })}
                        className="focus-ring type-label text-secondary hover:text-primary"
                      >
                        {t.purchases.viewCertificate}
                      </a>
                    </div>
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
